import {
  useEffect, useRef, useState,
  type ChangeEvent, type FormEvent, type KeyboardEvent,
} from 'react';
import { ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn, order } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoginBackdrop } from '@/components/LoginBackdrop';
import { LoginCompanion } from '@/components/companion/LoginCompanion';
import { Turnstile } from '@/components/Turnstile';
import { apiGet } from '@/lib/api';
import {
  LensIcon, WaveIcon, ArchiveIcon, ShieldPulseIcon, LinkPulseIcon,
} from '@/components/AnimatedIcons';

const WEEKDAY = ['日', '一', '二', '三', '四', '五', '六'];

/** 依時段問候，加上今天日期。只在掛載時算一次，跨午夜不必即時更新。 */
function greeting(now = new Date()) {
  const h = now.getHours();
  const word = h < 5 || h >= 23 ? '夜深了' : h < 11 ? '早安' : h < 18 ? '午安' : '晚安';
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const date = `${now.getFullYear()}/${pad(now.getMonth() + 1)}/${pad(now.getDate())}（週${WEEKDAY[now.getDay()]}）`;
  return { word, date, ymd };
}

type FieldErrors = { username?: string; password?: string };

/** 登入頁。
 *
 *  桌機：左側品牌敘事（深色動態背景）＋右側浮起的認證卡片，比例 55:45。
 *  手機：上方壓縮版品牌區（同樣的動態背景），下方表單 ——
 *        完全隱藏品牌只留白卡太單調，失去產品識別。
 *
 *  2026-10-18 人性化細節（自 Open Design 原型搬入）：
 *  - 受控表單加 noValidate：送出前逐欄檢查，空白時在欄位下方提示並把焦點放到第一個有問題的欄位，
 *    不交給瀏覽器的原生泡泡（各家樣式不一、手機上常被鍵盤遮住）。
 *  - 密碼欄偵測 Caps Lock，並可切換顯示密碼。
 *  - 帳密錯誤時兩個欄位一起輕晃一次，焦點回到密碼欄並選取既有內容，直接重打即可。
 *  - 卡片右上顯示今天日期，說明文字依時段問候。
 *  - 「忘記密碼」的處理方式直接寫在畫面上，不留一個沒有去處的連結。
 *  表單的 name（username / password / remember）沒有改；新增 login-user、login-pass、
 *  login-error 三個 id 供測試與輔助技術定位。
 */
export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [fieldErr, setFieldErr] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [shaking, setShaking] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>();
  const [turnstileToken, setTurnstileToken] = useState('');
  const [challengeAttempt, setChallengeAttempt] = useState(0);
  const [configAttempt, setConfigAttempt] = useState(0);
  const [configError, setConfigError] = useState('');
  const userRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);
  const [{ word, date, ymd }] = useState(greeting);

  useEffect(() => {
    let alive = true;
    setConfigError('');
    setTurnstileSiteKey(undefined);
    apiGet<{ turnstile_site_key: string | null }>('/api/v1/auth/config')
      .then((config) => {
        if (config.turnstile_site_key !== null && !config.turnstile_site_key) {
          throw new Error('登入驗證設定回應異常，請重試');
        }
        if (alive) setTurnstileSiteKey(config.turnstile_site_key);
      })
      .catch(() => {
        if (alive) setConfigError('無法載入登入驗證，請檢查網路後重試');
      });
    return () => { alive = false; };
  }, [configAttempt]);

  // 晃動是一次性的：動畫 380ms，420ms 後把 class 拿掉，下次錯誤才能再播。
  useEffect(() => {
    if (!shaking) return undefined;
    const t = window.setTimeout(() => setShaking(false), 420);
    return () => window.clearTimeout(t);
  }, [shaking]);

  const detectCaps = (e: KeyboardEvent<HTMLInputElement>) => {
    setCapsOn(typeof e.getModifierState === 'function' && e.getModifierState('CapsLock'));
  };

  const onUserChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (fieldErr.username) setFieldErr((f) => ({ ...f, username: undefined }));
  };
  const onPassChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (fieldErr.password) setFieldErr((f) => ({ ...f, password: undefined }));
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const user = username.trim();
    const errs: FieldErrors = {};
    if (!user) errs.username = '請輸入帳號';
    if (!password) errs.password = '請輸入密碼';
    setFieldErr(errs);
    setError('');
    if (errs.username) { userRef.current?.focus(); return; }
    if (errs.password) { passRef.current?.focus(); return; }
    if (turnstileSiteKey === undefined || configError) {
      setError(configError || '登入驗證準備中，請稍候');
      return;
    }
    if (turnstileSiteKey && !turnstileToken) {
      setError('請先完成人機驗證');
      return;
    }

    setBusy(true);
    try {
      await login(user, password, remember, turnstileToken);
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : '登入失敗';
      setError(msg.includes('未登入') ? '帳號或密碼錯誤' : msg);
      setBusy(false);
      setTurnstileToken('');
      setChallengeAttempt((value) => value + 1);
      setShaking(true);
      // 等 disabled 解除後再把焦點放回密碼欄並選取既有內容
      requestAnimationFrame(() => {
        passRef.current?.focus();
        passRef.current?.select();
      });
    }
  }

  const passHint = fieldErr.password
    ? { key: 'err', text: fieldErr.password, cls: 'text-destructive' }
    : capsOn
      ? { key: 'caps', text: 'Caps Lock 已開啟，密碼會區分大小寫', cls: 'text-warn-on' }
      : null;

  return (
    <div className="flex min-h-[100dvh] flex-col md:grid md:grid-rows-[minmax(330px,42dvh)_minmax(0,1fr)] lg:grid-cols-[55fr_45fr] lg:grid-rows-1">
      {/* ══ 手機版品牌區（桌機隱藏）══ */}
      <section className="relative isolate overflow-hidden bg-[#0A0A0C] px-5 pb-6 pt-7 sm:px-7 sm:pb-7 sm:pt-9 md:flex md:items-center md:py-10 lg:hidden">
        <LoginBackdrop />
        <div className="relative z-10 mx-auto w-full md:max-w-[680px]">
          <div className="login-enter flex items-center gap-3" style={order(0)}>
            {/* 深色底上原圖的 #BF1E2E 對比僅 3.2，用 CSS 提亮而非改圖，保留原始檔案 */}
            <img src="/assets/ai-guard-logo.png" alt="AI-Guard"
                 className="h-10 w-10 [filter:brightness(1.35)_saturate(1.15)]" />
            <div className="leading-tight">
              <strong className="block font-display text-[17px] font-bold text-white">
                EdgeAI
              </strong>
              <span className="font-mono text-[10px] tracking-[0.16em] text-white/55">
                FIELD OBSERVABILITY
              </span>
            </div>
            <span className="ml-auto flex items-center gap-1.5 rounded-full border
                             border-white/10 bg-white/[0.05] px-2.5 py-1">
              <LinkPulseIcon size={16} className="text-white/70" />
              <span className="font-mono text-[10px] tracking-[0.1em] text-white/60">
                EDGE AI
              </span>
            </span>
          </div>

          <h1 className="login-enter mt-5 font-display text-[25px] font-bold leading-[1.25] sm:text-[29px]
                         tracking-[-0.03em] text-white" style={order(1)}>
            讓每個現場訊號，
            <br />
            成為可以行動的證據。
          </h1>

          {/* 三個能力圖示橫排，保留產品識別又不佔空間 */}
          <div className="login-enter mt-5 grid grid-cols-3 gap-2 md:max-w-[540px]" style={order(2)}>
            {([
              { Icon: LensIcon, label: '視覺' },
              { Icon: WaveIcon, label: '聲學' },
              { Icon: ArchiveIcon, label: '歸檔' },
            ] as const).map(({ Icon, label }) => (
              <div key={label}
                   className="flex min-w-0 flex-col items-start gap-1 rounded-md border
                              border-white/[0.14] bg-white/[0.06] px-2.5 py-2 min-[360px]:flex-row min-[360px]:items-center min-[360px]:gap-2">
                <Icon size={22} className="shrink-0 text-white" />
                <span className="min-w-0 text-[12px] font-medium text-white/85">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 桌機版品牌敘事（手機隱藏）══ */}
      <section className="relative isolate hidden flex-col justify-between
                          overflow-hidden bg-[#0A0A0C] lg:px-10 lg:py-8 xl:px-14 xl:py-12 lg:flex">
        <LoginBackdrop />

        <div className="login-enter relative z-10 flex items-center justify-between gap-4" style={order(0)}>
          <div className="flex items-center gap-3.5">
            <img src="/assets/ai-guard-logo.png" alt="AI-Guard"
                 className="h-12 w-12 [filter:brightness(1.35)_saturate(1.15)]" />
            <div className="leading-tight">
              <strong className="block font-display text-[19px] font-bold text-white">
                EdgeAI
              </strong>
              <span className="font-mono text-[10px] tracking-[0.2em] text-white/45">
                FIELD OBSERVABILITY
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2.5 rounded-full border border-white/10
                          bg-white/[0.04] px-3.5 py-1.5">
            <LinkPulseIcon size={22} className="text-white/70" />
            <span className="font-mono text-[10px] tracking-[0.14em] text-white/45">
              CAMERA + AUDIO
            </span>
          </div>
        </div>

        <div className="relative z-10 max-w-[34rem]">
          <p className="login-enter font-mono text-[11px] tracking-[0.22em] text-red-400" style={order(1)}>
            EDGE INTELLIGENCE · FIELD SYSTEM
          </p>
          <h1 className="login-enter mt-4 font-display text-[38px] font-bold leading-[1.18]
                         xl:mt-6 xl:text-[46px]
                         tracking-[-0.03em] text-white" style={order(2)}>
            讓每個現場訊號，成為
            <br />
            可以行動的證據。
          </h1>
          <p className="login-enter mt-4 max-w-[30rem] text-[15px] leading-[1.75] text-white/60 xl:mt-6 xl:leading-[1.9]" style={order(3)}>
            從即時邊緣節點到可信任的影音歸檔，在同一個工作台掌握偵測、判讀與保存狀態。
          </p>

          <div className="login-enter mt-6 grid max-w-[34rem] grid-cols-3 gap-2.5 xl:mt-10 xl:gap-3" style={order(4)}>
            <Capability Icon={LensIcon} title="視覺節點" copy="影像與事件偵測" />
            <Capability Icon={WaveIcon} title="聲學節點" copy="聲響異常辨識" />
            <Capability Icon={ArchiveIcon} title="可信歸檔" copy="雜湊驗證與備份" />
          </div>
        </div>

        <p className="login-enter relative z-10 font-mono text-[10px] tracking-[0.22em] text-white/35" style={order(5)}>
          CAMERA · AUDIO · ARCHIVE
        </p>
      </section>

      {/* ══ 認證表單 ══ */}
      <section className="flex min-w-0 flex-1 flex-col items-center justify-start gap-4 bg-background
                          px-4 py-6 sm:px-7 sm:py-8 md:justify-center md:py-10 lg:px-6 lg:py-8 xl:py-12">
        <form
          onSubmit={onSubmit}
          noValidate
          className="login-card w-full max-w-[404px] rounded-lg border border-border bg-card
                     p-5 min-[360px]:p-6 sm:p-8 xl:p-9
                     shadow-[0_24px_60px_-28px_rgba(15,23,42,.4),0_2px_6px_rgba(15,23,42,.05)]"
        >
          <div className="flex items-start justify-between gap-3">
            <p className="font-mono text-[10px] tracking-[0.2em] text-primary">SECURE CONSOLE</p>
            <time dateTime={ymd} className="tabular shrink-0 font-mono text-[10.5px] text-muted-foreground">
              {date}
            </time>
          </div>
          <LoginCompanion word={word} passwordFocused={passwordFocused} busy={busy}
            hasError={Boolean(error || fieldErr.username || fieldErr.password)} />

          {/* 兩個欄位包在同一層，帳密錯誤時一起輕晃 */}
          <div className={cn('login-fields', shaking && 'is-shaking')}>
            <div className="mt-7">
              <label htmlFor="login-user" className="mb-2 block text-[12.5px] font-semibold">帳號</label>
              <Input ref={userRef} id="login-user" name="username" type="text"
                     autoComplete="username" autoFocus
                     value={username} onChange={onUserChange}
                     aria-invalid={fieldErr.username ? true : undefined}
                     aria-describedby={fieldErr.username ? 'login-user-hint' : undefined}
                     className={cn('text-base sm:text-sm', fieldErr.username && 'border-destructive')}
                     placeholder="輸入帳號" />
              {fieldErr.username && (
                <p id="login-user-hint" className="login-hint mt-1.5 text-[12px] font-medium text-destructive">
                  {fieldErr.username}
                </p>
              )}
            </div>

            <div className="mt-4">
              <label htmlFor="login-pass" className="mb-2 block text-[12.5px] font-semibold">密碼</label>
              <div className="relative" onFocusCapture={() => setPasswordFocused(true)}
                   onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setPasswordFocused(false); }}>
                <Input ref={passRef} id="login-pass" name="password"
                       type={showPass ? 'text' : 'password'}
                       autoComplete="current-password"
                       value={password} onChange={onPassChange}
                       onKeyDown={detectCaps} onKeyUp={detectCaps}
                       onBlur={() => setCapsOn(false)}
                       aria-invalid={fieldErr.password ? true : undefined}
                       aria-describedby="login-pass-hint"
                       className={cn('pr-12 text-base sm:text-sm', fieldErr.password && 'border-destructive')}
                       placeholder="輸入密碼" />
                {/* 44px 方塊貼齊輸入框右側，手機也按得準 */}
                <button type="button" onClick={() => setShowPass((v) => !v)}
                        aria-pressed={showPass}
                        aria-label={showPass ? '隱藏密碼' : '顯示密碼'}
                        title={showPass ? '隱藏密碼' : '顯示密碼'}
                        className="login-eye absolute inset-y-0 right-0 grid w-11 place-items-center
                                   rounded-sm text-muted-foreground transition-colors
                                   hover:text-foreground focus-visible:outline-none
                                   focus-visible:ring-2 focus-visible:ring-ring">
                  {showPass ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
              {/* 固定存在的 status 區，欄位錯誤優先，其次是 Caps Lock 提醒 */}
              <p id="login-pass-hint" role="status" aria-live="polite"
                 className="text-[12px] font-medium">
                {passHint && (
                  <span key={passHint.key} className={cn('login-hint mt-1.5 block', passHint.cls)}>
                    {passHint.text}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* 勾選後後端發長效 cookie，工作階段存在資料庫，API 重啟也不掉 */}
          <label className="mt-5 flex min-h-[44px] cursor-pointer items-center gap-2.5 text-[13px]">
            <input type="checkbox" name="remember" checked={remember}
                   onChange={(e) => setRemember(e.target.checked)}
                   className="h-[17px] w-[17px] rounded-[3px] accent-primary" />
            <span>30 天內保持登入</span>
          </label>

          {turnstileSiteKey && (
            <Turnstile key={challengeAttempt} siteKey={turnstileSiteKey} onVerify={setTurnstileToken} />
          )}
          {turnstileSiteKey === undefined && !configError && (
            <p role="status" className="text-xs text-muted-foreground">正在準備登入驗證…</p>
          )}
          {configError && (
            <div className="text-xs text-destructive">
              <p role="alert">{configError}</p>
              <Button type="button" variant="link" size="sm" onClick={() => setConfigAttempt((value) => value + 1)}>
                重新載入
              </Button>
            </div>
          )}

          <p id="login-error" role="alert" aria-live="polite"
             className="login-error min-h-[20px] text-[12.5px] font-semibold text-destructive">
            {error}
          </p>

          <Button type="submit" block
            disabled={busy || turnstileSiteKey === undefined || Boolean(configError) || Boolean(turnstileSiteKey && !turnstileToken)}
            aria-busy={busy} className="btn-go mt-1.5">
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>驗證身分中…</span>
              </>
            ) : (
              <>
                <span>登入</span>
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>

          <p className="mt-4 text-[12px] leading-relaxed text-muted-foreground">
            忘記密碼？平台不提供自助重設，請聯絡系統管理員為您重設。
          </p>
        </form>

        <p className="login-enter flex max-w-[404px] items-start gap-2 px-2 text-left text-xs leading-relaxed text-muted-foreground sm:items-center sm:text-center" style={order(6)}>
          <ShieldPulseIcon size={15} className="shrink-0 text-muted-foreground/70" />
          受保護的內部工作台 · 依角色與場域權限存取
        </p>
      </section>
    </div>
  );
}

function Capability({
  Icon,
  title,
  copy,
}: {
  Icon: (p: { size?: number; className?: string }) => JSX.Element;
  title: string;
  copy: string;
}) {
  return (
    <div className="min-w-0 rounded-md border border-white/[0.14] bg-white/[0.06] px-3 py-3
                    xl:px-4 xl:py-4
                    backdrop-blur-sm transition-colors
                    hover:border-white/25 hover:bg-white/[0.1]">
      <Icon size={34} className="text-white" />
      <strong className="mt-3 block text-[13px] font-semibold text-white">{title}</strong>
      <span className="mt-0.5 block break-words text-[11px] leading-snug text-white/55">{copy}</span>
    </div>
  );
}
